import React, { useState, useEffect } from 'react';
import { TextField, Grid, Typography, CircularProgress } from '@mui/material';
import axios from 'axios';
import { showToast } from '../../utils/toastUtils';
import ActionButton from '../../components/ActionButton';
import { useSelector } from 'react-redux';

const OrganizationDetailsForm = ({ isLoading }) => {
  const userIdFromRedux = useSelector(state => state.onboarding.userId);
  const userId = userIdFromRedux || localStorage.getItem('userId');

  const [formData, setFormData] = useState({
    organisation: '',
    address: '',
    sector: '',
    industry: '',
    servicesProducts: '',
    areaOfOperation: '',
    phone: '',
    organizationSize: '',
    website: '',
  });

  const [initialFormData, setInitialFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    const fetchOrganizationData = async () => {
      if (!userId) {
        showToast("User not found. Please log in again.", "error");
        return;
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/user/${userId}`);
        if (response.status === 200) {
          setFormData(response.data);
          setInitialFormData(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch organization data", error);
        showToast("Failed to load organization data", "error");
      }
    };

    const fetchBranches = async () => {
      if (!userId) return;

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/branch/branches/${userId}`);
        if (response.data.branches) {
          const branchesWithDepartments = response.data.branches.map(branch => ({
            ...branch,
            departments: branch.departments || [],
            name: branch.name || '',
            address: branch.address || '',
            country: branch.country || '',
            state: branch.state || '',
            pinCode: branch.pinCode || ''
          }));
          setBranches(branchesWithDepartments);
        }
      } catch (error) {
        showToast("Failed to load branches", "error");
      }
    };

    fetchOrganizationData();
    fetchBranches();
  }, [userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.organisation.trim()) newErrors.organisation = 'Organization name is required';
    if (!formData.address.trim()) newErrors.address = 'Organization address is required';
    if (formData.website && !/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/.test(formData.website)) {
      newErrors.website = 'Invalid website URL';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm() && !loading) {
      try {
        setLoading(true);
        const changedFields = Object.keys(formData).filter(key => formData[key] !== initialFormData[key]);

        if (changedFields.length === 0) {
          showToast("No changes", "info");
          return;
        }

        const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/user/profile`, {
          oldUserProfile: initialFormData,
          newUserProfile: formData
        });

        if (response.data.message !== 'No fields were updated') {
          setFormData(response.data);
          setInitialFormData(response.data);
          showToast("Organization details updated successfully", "success");
        }
      } catch (error) {
        console.error("Error updating organization details", error);
        showToast("Error updating organization details", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Typography variant="h6" gutterBottom>Organization Details</Typography>
        <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Organization Name"
            name="organisation"
            value={formData.organisation}
            onChange={handleChange}
            required
            error={!!errors.organisation}
            helperText={errors.organisation}
            disabled={loading || isLoading}
            sx={{ marginBottom: 2 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Organization Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
            error={!!errors.address}
            helperText={errors.address}
            disabled={loading || isLoading}
            sx={{ marginBottom: 2 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Sector"
            name="sector"
            value={formData.sector}
            onChange={handleChange}
            disabled={loading || isLoading}
            sx={{ marginBottom: 2 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Industry"
            name="industry"
            value={formData.industry}
            onChange={handleChange}
            disabled={loading || isLoading}
            sx={{ marginBottom: 2 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Services/Products"
            name="servicesProducts"
            value={formData.servicesProducts}
            onChange={handleChange}
            disabled={loading || isLoading}
            sx={{ marginBottom: 2 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Area of Operation"
            name="areaOfOperation"
            value={formData.areaOfOperation}
            onChange={handleChange}
            disabled={loading || isLoading}
            sx={{ marginBottom: 2 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Branches"
            name="branches"
            value={branches.map(branch => branch.name).join(', ')}
            disabled={true}
            sx={{ marginBottom: 2 }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            disabled={loading || isLoading}
            sx={{ marginBottom: 2 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Organization Size"
            name="organizationSize"
            value={formData.organizationSize}
            onChange={handleChange}
            disabled={loading || isLoading}
            sx={{ marginBottom: 2 }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Website"
            name="website"
            value={formData.website}
            onChange={handleChange}
            error={!!errors.website}
            helperText={errors.website}
            disabled={loading || isLoading}
            sx={{ marginBottom: 2 }}
          />
        </Grid>
        <Grid item xs={12}>
          <ActionButton
            text={loading ? <CircularProgress size={24} /> : 'Save'}
            onClick={handleSubmit}
          />
        </Grid>
        </Grid>
      </form>
    </>
  );
};

export default OrganizationDetailsForm;
